import React, { ChangeEvent } from 'react'
import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useFormikContext } from 'formik'

import { ProfileFormValues } from '../config'

import ProfileFormWrapper from 'components/wrappers/ProfileFormWrapper'

const useStyles = makeStyles((theme) => ({
  info: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 16,
    padding: 16,
    marginTop: 24,

    overflowY: 'auto',

    maxHeight: '500px',
  },
  text: {
    lineHeight: '20px',
    marginBottom: 16,

    '&:last-child': {
      margin: 0,
    },
  },
  link: {
    textDecoration: 'underscore',
    color: theme.palette.primary.main,
  },
  checkbox: {
    paddingTop: 0,
  },
  error: {
    fontSize: 13,
    color: theme.palette.error.main,
  },
  typedSignature: {
    margin: '30px 0',
  },
}))

const ProfileFormAgreement = () => {
  const classes = useStyles()
  const { setFieldValue, values, errors } =
    useFormikContext<ProfileFormValues>()

  return (
    <ProfileFormWrapper title="Creator Agreement">
      <Grid className={classes.info}>
        <Typography
          className={classes.text}
          sx={{ textTransform: 'uppercase' }}
        >
          <b>CREATOR AGREEMENT AND RELEASE</b>
        </Typography>
        <Typography className={classes.text}>
          To: Hue Beauty, Inc. (the <b>“Company”</b> or <b>“you”</b>)
        </Typography>
        <Typography className={classes.text}>
          In view of the fact that I may be personally participating in the
          creation of audio, video and other works, you have asked me to confirm
          that I have given you permission to use my name, likeness, voice, and
          image (<b>“Likeness”</b>), and certain other matters described below.
          Accordingly, by submitting my name and clicking “I accept” or similar,
          I hereby agree as follows:
        </Typography>
        <Typography className={classes.text}>
          <b>My participation in the Campaign:</b> I intend to participate in
          the brand campaign described to me by email or by contacting a Hue
          Community Manager on Discord (the <b>“Campaign”</b>). My participation
          will be in compliance with the materials and instructions provided to
          me by you (the <b>“Campaign Materials”</b>), including without
          limitation the Company’s Campaign Guidelines and Tips applicable to
          the Campaign(s) I participate in. In exchange for my participation in
          compliance with this agreement, I will receive the products described
          in the Campaign Materials. If I decide not to participate in the
          Campaign, my obligations under this agreement with respect to the
          creation of Content will be void (excluding the Company’s rights to
          already provided Content, as provided below), and the Company will
          have no obligation to provide me with products or other compensation.
          I will use reasonable efforts to promptly notify you by email of any
          such decision.
        </Typography>
        <Typography className={classes.text}>
          <b>You will own any Content I create for a Campaign:</b> This
          agreement applies to all video, audio, pictures and other digital or
          film recordings of me, including without limitation, photographs,
          images, motion pictures, video and sound recordings, and soundbites
          and quotes from the foregoing, that I provide pursuant to the Campaign
          Materials (<b>“Content”</b>). I understand and agree that any Content
          I provide to you, in any format, and all rights therein, will, upon
          development, be the sole and exclusive property of the Company, and
          this agreement will be deemed and hereby is an irrevocable assignment
          to the Company of all rights in the Content, including copyright. I
          will, at your request, execute all documents as are required to vest
          such rights in the Company. I represent that I have all rights and
          licenses necessary to provide the Content to you in accordance with
          this agreement, and I will not include any materials in the Content to
          which I don’t have such rights. For the avoidance of doubt, I
          understand that you are not making any claim to, nor shall you have
          any ownership of, my Likeness, which shall only be licensed to you in
          connection with the Content as described below.
        </Typography>
        <Typography className={classes.text}>
          <b>
            You can use the Content in connection with products and services:
          </b>{' '}
          I acknowledge that as the owner of the Content, you have the
          unrestricted right to create, publish, make derivative works of, and
          otherwise use and permit others to use or transfer ownership of, all
          Content, in color or otherwise, in any medium or media, whether now
          known or hereinafter known, including your web site, software
          applications, tools, products, and services, and in each of the above
          cases to provide such Content in connection with your products and
          services to others, including any brands you may be working with in
          connection with the Campaign. In connection with your ownership rights
          in the Content, I hereby grant you a non-exclusive, worldwide,
          royalty-free, irrevocable, perpetual, transferable license to use my
          Likeness, including the right to sublicense such rights to any of your
          customers, clients or other third parties in connection with their use
          of the Content.
        </Typography>
        <Typography className={classes.text}>
          <b>
            You can edit the Content for production reasons and use it with
            other materials:
          </b>{' '}
          I specifically consent to the alterations as to size (including length
          of video), color and shape in a reasonable manner to conform and adapt
          materials to the requirements of devices, services or media (e.g.
          shrinking or cropping images). I also specifically consent to the use
          of the Content in conjunction with my own or any other image, product,
          person, or name. I waive the right to inspect or approve the finished
          product or service incorporating the Content. I also waive any moral
          rights I might have in the Content, including rights of integrity and
          attribution.
        </Typography>
        <Typography className={classes.text}>
          <b>I won’t sue you:</b> I agree that I shall be entitled to no
          consideration as a result of the exercise of the rights granted
          herein, except for the product(s) and any other compensation expressly
          set forth in the Campaign Materials, subject to the terms therein. I
          hereby waive all rights or claims that I may have against you in
          connection with, and release you from all liability and obligation to
          me of any nature whatsoever arising out of or in connection with, the
          Content or the exercise of the rights granted above, including without
          limitation, all claims and liability for violation of any rights of
          privacy, publicity, defamation or any similar right. The Company shall
          have no liability to me for direct, indirect, consequential, special,
          punitive or other damages, without limiting the Company’s obligation
          to provide the consideration referenced in the first sentence of this
          paragraph.
        </Typography>
        <Typography className={classes.text}>
          <b>Information:</b> I have used reasonable efforts to ensure that any
          information I have provided to you in connection with my application
          to participate in the Campaign is accurate. I agree to protect all
          Campaign Materials as your confidential information, and that I will
          not disclose the Campaign Materials to any third party; provided that
          these obligations shall not apply to those Campaign Materials that are
          in the public domain through no fault of my own.
        </Typography>
        <Typography className={classes.text}>
          <b>My Use of My Content:</b> I understand that in addition to
          submitting my Content to the Company in connection with the Campaign,
          the Company is granting me a perpetual, fully paid up right to use and
          to post the Content on my own social media and in doing so I accept
          and agree that my posts or display of the Content are subject to the
          “I won’t sue you” section of this agreement. For instance, if I make
          an untrue claim about a product or fail to disclose any compensation I
          may receive from the Content and legal action is pursued against me, I
          cannot sue the Company or the applicable brands. In addition, I
          understand and agree that I will be responsible for complying with
          applicable laws in posting the Content, including for example,
          disclosures that I was compensated for making the Content.
        </Typography>
        <Typography className={classes.text}>
          <b>Timing and Confidentiality:</b> Notwithstanding the “My Use of My
          Content” section above, I understand and agree that certain Campaigns
          may provide me with products or services that are not publicly
          available and in such case I understand and agree that I may not use
          the Content I create until permitted to do so under the timing and
          terms of the applicable Campaign Materials.
        </Typography>
        <Typography className={classes.text}>
          <b>Privacy:</b> I understand that my personal data or personal
          information, as defined by applicable law, including certain
          information that may be deemed sensitive information, such as my
          gender, gender identity, and ethnicity, will be handled as provided in
          the Company’s privacy policy located at:{' '}
          <a
            href="https://www.poweredbyhue.com/privacy-policy"
            target="_blank"
            className={classes.link}
          >
            https://www.poweredbyhue.com/privacy-policy
          </a>
          .
        </Typography>
        <Typography className={classes.text}>
          <b>General:</b> This agreement is governed by the laws of the State of
          Delaware, without reference to its rules governing conflicts of laws.
          This agreement may not be assigned or transferred by me to any other
          party, nor may any work be subcontracted by me. Nothing contained in
          this agreement shall be construed to constitute or imply any
          employment relationship between the Company and me.
        </Typography>
      </Grid>
      <Grid mt={3.5} container flexDirection="column" gap={2}>
        <Grid>
          <FormControlLabel
            sx={{ alignItems: 'flex-start' }}
            control={
              <Checkbox
                color="mint"
                size="small"
                className={classes.checkbox}
                disableRipple
                checked={values.communityGuidesAccepted}
                onChange={(e: ChangeEvent, checked) =>
                  setFieldValue('communityGuidesAccepted', checked)
                }
              />
            }
            label={
              <Typography
                sx={{ fontWeight: values.communityGuidesAccepted ? 500 : 400 }}
              >
                I understand that in exchange for my honest video review, I will
                receive full-sized product(s) in exchange for participation. I
                understand that I will not receive any monetary compensation
                from my participation in Hue.
              </Typography>
            }
          />
          {errors?.communityGuidesAccepted && (
            <Typography className={classes.error}>
              {errors.communityGuidesAccepted}
            </Typography>
          )}
        </Grid>
        <Grid>
          <FormControlLabel
            sx={{ alignItems: 'flex-start' }}
            control={
              <Checkbox
                color="mint"
                size="small"
                className={classes.checkbox}
                disableRipple
                checked={values.TnCAccepted}
                onChange={(event, checked) =>
                  setFieldValue('TnCAccepted', checked)
                }
              />
            }
            label={
              <Typography sx={{ fontWeight: values.TnCAccepted ? 500 : 400 }}>
                I understand that the video reviews that I submit to Hue are
                intended to be used on brand's website in the ratings & reviews
                to help customers assess products. I understand that a video
                review campaign is complete when I submit the video to Hue, and
                that I am NOT required to post on my own social media.
              </Typography>
            }
          />
          {errors?.TnCAccepted && (
            <Typography className={classes.error}>
              {errors.TnCAccepted}
            </Typography>
          )}
        </Grid>
        <Grid>
          <FormControlLabel
            sx={{ alignItems: 'flex-start' }}
            control={
              <Checkbox
                color="mint"
                size="small"
                className={classes.checkbox}
                disableRipple
                checked={values.willingAccepted}
                onChange={(event, checked) =>
                  setFieldValue('willingAccepted', checked)
                }
              />
            }
            label={
              <Typography
                sx={{ fontWeight: values.willingAccepted ? 500 : 400 }}
              >
                I am willing and comfortable filming and taking photos of myself
                to complete video reviews.
              </Typography>
            }
          />
          {errors?.willingAccepted && (
            <Typography className={classes.error}>
              {errors.willingAccepted}
            </Typography>
          )}
        </Grid>
      </Grid>
    </ProfileFormWrapper>
  )
}

export default ProfileFormAgreement
